import axios from 'axios';

// Base URL for the API - loaded from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

/**
 * Get active services for a corporation
 * @param {string} corporationId - Corporation UUID
 * @returns {Promise} Promise with services data
 */
export const getServices = async (corporationId) => {
  try {
    const response = await api.get('/services', {
      params: {
        corporation_id: corporationId,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get active staffs for a corporation
 * @param {string} corporationId - Corporation UUID
 * @returns {Promise} Promise with staffs data
 */
export const getStaffs = async (corporationId) => {
  try {
    const response = await api.get('/staffs', {
      params: {
        corporation_id: corporationId,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get available time slots
 * @param {string} corporationId - Corporation UUID
 * @param {string} staffId - Staff UUID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {Array<string>} serviceIds - Array of service UUIDs
 * @returns {Promise} Promise with available time slots
 */
export const getAvailableTimeSlots = async (corporationId, staffId, date, serviceIds) => {
  try {
    // Validate inputs
    if (!corporationId || !staffId || !date) {
      throw new Error('Missing required parameters: corporationId, staffId, or date');
    }
    
    if (!serviceIds || serviceIds.length === 0) {
      throw new Error('At least one service ID is required');
    }

    // Build query string manually to preserve literal brackets []
    // URLSearchParams encodes [] as %5B%5D, but API needs literal []
    const params = [
      `corporation_id=${encodeURIComponent(corporationId)}`,
      `staff_id=${encodeURIComponent(staffId)}`,
      `date=${encodeURIComponent(date)}`,
    ];
    
    // Add service_ids[] with literal brackets (not URL-encoded)
    serviceIds.forEach((id) => {
      params.push(`service_ids[]=${encodeURIComponent(id)}`);
    });

    const queryString = params.join('&');
    
    const response = await api.get(`/availability/time-slots?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching time slots:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw error;
  }
};

/**
 * Create a booking
 * @param {Object} bookingData - Booking data object
 * @returns {Promise} Promise with booking response
 */
export const createBooking = async (bookingData) => {
  try {
    const response = await api.post('/bookings', bookingData);
    return response.data;
  } catch (error) {
    console.error('Error creating booking:', error);
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw error;
  }
};

export default api;

